import { useState } from 'react';
import { ImageOff } from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { cn } from '~/lib/utils';

interface ProductThumbProps {
	src?: string | null;
	alt: string;
	/** Compact density shrinks the thumb to fit a denser row. */
	compact?: boolean;
}

interface BwEnv {
	REACT_APP_IMAGES_EXT?: string;
}

/**
 * Derive the full-size image URL from a thumbnail URL: drop the size suffix (_S/_M/_L) and swap the
 * `.webp` thumb extension for the raw extension. Mirrors the legacy admin's `getRawImageUrl`
 * (ImageCell.js) — raw images are stored under the unsuffixed name with `REACT_APP_IMAGES_EXT`.
 */
function toRawImageUrl(src: string): string {
	const ext = (globalThis as { __BW_ENV__?: BwEnv }).__BW_ENV__?.REACT_APP_IMAGES_EXT ?? '.jpg';
	return src.replace('_S.', '.').replace('_M.', '.').replace('_L.', '.').replace('.webp', ext);
}

/**
 * 48×60 product cover thumb. Clicking it opens a lightbox with the full-size image (like the legacy
 * admin's image cell). Falls back to a muted placeholder icon when there is no image.
 */
export function ProductThumb({ src, alt, compact }: ProductThumbProps) {
	const [open, setOpen] = useState(false);

	const sizeClass = compact ? 'h-12 w-10' : 'h-[60px] w-12';

	if (!src) {
		return (
			<span
				className={cn(
					'flex shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border bg-surface text-foreground-disabled',
					sizeClass
				)}>
				<ImageOff size={16} />
			</span>
		);
	}

	return (
		<>
			<button
				type="button"
				title={alt}
				onClick={e => {
					// Don't let the click bubble to the row (which navigates to the editor).
					e.stopPropagation();
					setOpen(true);
				}}
				className={cn(
					'flex shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-sm border border-border bg-surface',
					sizeClass
				)}>
				<img src={src} alt={alt} className="h-full w-full object-contain" loading="lazy" />
			</button>
			<Lightbox
				open={open}
				close={() => setOpen(false)}
				slides={[{ src: toRawImageUrl(src), alt }]}
				carousel={{ finite: true }}
				render={{ buttonPrev: () => null, buttonNext: () => null }}
			/>
		</>
	);
}
